// src/components/JournalStore.js
import React from "react";
import journals from "../data/journals";
import PreviewModal from "./PreviewModal";
import "./JournalStore.css";
import { createRazorpayOrder, verifyRazorpayPayment } from "../utils/payments";

// Firebase Storage (to open the PDF after payment)
import { storage } from "../firebase";
import { ref, getDownloadURL } from "firebase/storage";

const LIVE_KEY_ID = "rzp_live_R5DnQbQDXpHkMe";
const DISPLAY_LANGUAGE = "English";

/** "₹29" → 29 */
function toRupees(v) {
  if (typeof v === "number") return v;
  const n = parseFloat(String(v ?? "").replace(/[^\d.]/g, ""));
  return Number.isFinite(n) ? n : NaN;
}

/** Derive a Firebase Storage path from a journal.downloadUrl (fallback) */
function deriveStoragePath(downloadUrl) {
  if (!downloadUrl) return undefined;
  try {
    const path = new URL(downloadUrl, window.location.origin).pathname.replace(/^\/+/, "");
    return path.startsWith("downloads/") ? path : `downloads/${path}`;
  } catch {
    const raw = String(downloadUrl || "").replace(/^\/+/, "");
    return raw.startsWith("downloads/") ? raw : `downloads/${raw}`;
  }
}

/** Get a public URL for a given storage path */
async function getPdfUrl(storagePath) {
  if (!storagePath) throw new Error("Missing storage path for PDF.");
  return await getDownloadURL(ref(storage, storagePath));
}

/** Open/download a PDF and return the URL we used */
async function openPdfFromStorage(storagePath, mode = "download") {
  const url = await getPdfUrl(storagePath);

  if (mode === "tab") {
    window.open(url, "_blank", "noopener,noreferrer");
  } else {
    // Force a file download (more reliable on mobile than window.open)
    const a = document.createElement("a");
    a.href = url;
    a.download = storagePath.split("/").pop() || "journal.pdf";
    document.body.appendChild(a);
    a.click();
    a.remove();
  }
  return url;
}

function JournalStore() {
  const [selectedJournal, setSelectedJournal] = React.useState(null);
  const [showModal, setShowModal] = React.useState(false);
  const [payLoadingId, setPayLoadingId] = React.useState(null);

  const openModal = (journal) => {
    setSelectedJournal(journal);
    setShowModal(true);
  };

  const closeModal = () => {
    setSelectedJournal(null);
    setShowModal(false);
  };

  // Card "Buy Now" → Razorpay (no preview)
  async function handleBuyNow(journal) {
    if (!window.Razorpay) {
      alert("Payment module not loaded. Please refresh and try again.");
      return;
    }

    try {
      setPayLoadingId(journal.title);

      const rupees = toRupees(journal.price);
      if (!rupees || rupees <= 0) throw new Error("Invalid amount.");

      const storagePath = journal.storagePath || deriveStoragePath(journal.downloadUrl);

      // 1) Create order via your backend
      const order = await createRazorpayOrder(rupees, {
        journalTitle: journal.title,
        language: DISPLAY_LANGUAGE,           // ✅ English only
        downloadUrl: journal.downloadUrl || "",
        storagePath,                          // used for auto-download + dashboard note
      });

      if (!order?.id || !order?.amount) {
        throw new Error("Failed to create order. Please try again.");
      }

      // 2) Open Razorpay checkout
      const options = {
        key: LIVE_KEY_ID,
        amount: order.amount,
        currency: order.currency || "INR",
        name: "WhisperWall",
        description: journal.title,
        order_id: order.id,
        prefill: { name: "", email: "", contact: "" },
        notes: {
          journalTitle: journal.title,
          language: DISPLAY_LANGUAGE,         // ✅ English only
          storagePath,
        },
        theme: { color: "#7C3AED" },

        // 3) Success → verify in background + open the PDF immediately
        handler: async function (resp) {
          try {
            // Save params so ThankYou can kick a backup verify if needed
            sessionStorage.setItem("rzp_verification", JSON.stringify(resp));

            // Fire-and-forget verification; 'keepalive' keeps it running during navigation
            verifyRazorpayPayment(
              {
                razorpay_payment_id: resp.razorpay_payment_id,
                razorpay_order_id: resp.razorpay_order_id,
                razorpay_signature: resp.razorpay_signature,
              },
              { keepalive: true }
            ).catch(() => { /* non-blocking */ });

            // 3a) Download immediately and capture the direct URL
            let directUrl = "";
            try {
              if (storagePath) {
                directUrl = await openPdfFromStorage(storagePath, "download");
              } else if (journal.downloadUrl) {
                directUrl = journal.downloadUrl;
                window.open(directUrl, "_blank", "noopener,noreferrer");
              }
            } catch (e) {
              console.warn("Auto-open PDF failed:", e);
            }

            // 3b) Navigate to Thank You. Pass both storagePath and directUrl to skip extra work there.
            const qs = new URLSearchParams({
              pid: resp.razorpay_payment_id || "",
              sp: storagePath || "",
              du: directUrl || ""
            }).toString();

            // Small delay gives browsers time to register the download click before navigation
            setTimeout(() => {
              window.location.replace(`/thank-you?${qs}`);
            }, 150);
          } catch (e) {
            console.error(e);
            alert(e.message || "Verification failed. Please contact support.");
          }
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (err) {
      console.error(err);
      alert(err.message || "Payment failed. Please try again.");
    } finally {
      setPayLoadingId(null);
    }
  }

  return (
    <div className="store-container">
      <h2 className="store-heading">Explore All Journals</h2>

      <p className="emotional-tagline">
        “These aren’t just journals. They’re emotional lifelines — crafted with
        care, priced with kindness, made to help you heal.”
      </p>

      <div className="journal-grid">
        {journals.map((journal, index) => (
          <div className="journal-card" key={index}>
            <img
              src={journal.image}
              alt={journal.title}
              className="journal-image"
              loading="lazy"
              decoding="async"
              fetchpriority={index < 2 ? "high" : "auto"}
              width="300"
              height="400"
            />
            <h3 className="journal-title">{journal.title}</h3>
            <div className="button-group">
              <button className="preview-btn" onClick={() => openModal(journal)}>
                Preview
              </button>
              <button
                className="buy-btn"
                onClick={() => handleBuyNow(journal)}
                disabled={payLoadingId === journal.title}
                aria-busy={payLoadingId === journal.title ? "true" : "false"}
              >
                {payLoadingId === journal.title ? "Processing…" : "Buy Now"}
              </button>
            </div>
          </div>
        ))}
      </div>

      {showModal && (
        <PreviewModal journal={selectedJournal} onClose={closeModal} />
      )}
    </div>
  );
}

export default JournalStore;
